import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Plus, Scale, Edit, Trash2, Search, Eye, Settings, Briefcase, Clock, Gavel, Archive, Lock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Máscara de moeda: o campo guarda sempre um decimal limpo ("1500.50"); a exibição formata
// esse valor como "1.500,50" e a digitação funciona por dígitos (os 2 últimos são os centavos).
const formatCurrencyDisplay = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(num) ? '' : num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseCurrencyInput = (rawText) => {
  const digits = rawText.replace(/\D/g, '');
  return digits ? (parseInt(digits, 10) / 100).toFixed(2) : '';
};

const TIPO_PROCESSO_OPTIONS = ['Judicial', 'Administrativo', 'Extrajudicial', 'Arbitragem'];
const STATUS_OPTIONS = ['Ativo', 'Suspenso', 'Encerrado', 'Arquivado'];
const RISCO_OPTIONS = ['Baixo', 'Médio', 'Alto'];
const GRAU_SIGILO_OPTIONS = ['Público', 'Restrito', 'Confidencial'];
const PROBABILIDADE_PERDA_OPTIONS = ['Remota', 'Possível', 'Provável', 'Alta'];
const BOARD_COLOR_OPTIONS = ['Roxo', 'Azul', 'Verde', 'Laranja', 'Vermelho'];

const STATUS_COLOR = {
  'Ativo': 'bg-blue-100 text-blue-800',
  'Suspenso': 'bg-yellow-100 text-yellow-800',
  'Encerrado': 'bg-gray-100 text-gray-800',
  'Arquivado': 'bg-gray-100 text-gray-600',
};

const RISCO_COLOR = {
  'Baixo': 'bg-green-100 text-green-800',
  'Médio': 'bg-orange-100 text-orange-800',
  'Alto': 'bg-red-100 text-red-800',
};

const EMPTY_FORM = {
  titulo: '',
  numero_processo: '',
  codigo_interno: '',
  tipo_processo: 'Judicial',
  tipo_acao: '',
  classe_acao: '',
  assunto_principal: '',
  tribunal: '',
  comarca: '',
  vara: '',
  juiz: '',
  status: 'Ativo',
  risco: 'Médio',
  situacao_atual: '',
  grau_sigilo: 'Restrito',
  probabilidade_perda: '',
  valor_causa: '',
  valor_provisionado: '',
  resumo_caso: '',
  tese_principal: '',
  contract_id: '',
  board_id: '',
};

const EMPTY_BOARD_FORM = {
  name: '',
  entity: '',
  document: '',
  responsible: '',
  color: 'Roxo',
  locked: false,
  users: '',
  description: '',
};

const GestaoProcessosPage = () => {
  const navigate = useNavigate();
  const [processos, setProcessos] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProcesso, setEditingProcesso] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [showBoardFormModal, setShowBoardFormModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [boardFormData, setBoardFormData] = useState(EMPTY_BOARD_FORM);
  const [savingBoard, setSavingBoard] = useState(false);

  useEffect(() => {
    loadProcessos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, selectedBoardId]);

  useEffect(() => {
    loadContracts();
    loadBoards();
  }, []);

  const loadProcessos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      if (selectedBoardId) params.board_id = selectedBoardId;
      const response = await axios.get(`${API}/api/processos-juridicos`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setProcessos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar processos jurídicos');
    } finally {
      setLoading(false);
    }
  };

  // Lista de contratos para vínculo opcional — se o usuário não tiver permissão
  // sobre contratos, o select fica simplesmente vazio (vínculo é opcional).
  const loadContracts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContracts(response.data);
    } catch (error) {
      setContracts([]);
    }
  };

  const loadBoards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/processos-boards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBoards(response.data);
    } catch (error) {
      setBoards([]);
    }
  };

  const selectedBoard = boards.find((b) => b.id === selectedBoardId) || null;

  const filteredProcessos = processos.filter((p) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      p.titulo?.toLowerCase().includes(term) ||
      p.numero_processo?.toLowerCase().includes(term) ||
      p.codigo_interno?.toLowerCase().includes(term)
    );
  });

  const openCreateModal = () => {
    setEditingProcesso(null);
    setFormData({ ...EMPTY_FORM, board_id: selectedBoardId || '' });
    setShowFormModal(true);
  };

  const openEditModal = (processo) => {
    setEditingProcesso(processo);
    setFormData({
      ...EMPTY_FORM,
      ...processo,
      valor_causa: processo.valor_causa ?? '',
      valor_provisionado: processo.valor_provisionado ?? '',
      contract_id: processo.contract_id || '',
      board_id: processo.board_id || '',
      probabilidade_perda: processo.probabilidade_perda || '',
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingProcesso(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titulo.trim()) {
      toast.error('Informe o título do processo');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        valor_causa: formData.valor_causa ? parseFloat(formData.valor_causa) : null,
        valor_provisionado: formData.valor_provisionado ? parseFloat(formData.valor_provisionado) : null,
        contract_id: formData.contract_id || null,
        board_id: formData.board_id || null,
        probabilidade_perda: formData.probabilidade_perda || null,
      };

      if (editingProcesso) {
        await axios.put(`${API}/api/processos-juridicos/${editingProcesso.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Processo atualizado com sucesso');
      } else {
        await axios.post(`${API}/api/processos-juridicos`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Processo criado com sucesso');
      }

      closeFormModal();
      loadProcessos();
      loadBoards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar processo jurídico');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (processo) => {
    if (!window.confirm(`Excluir o processo "${processo.titulo}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/processos-juridicos/${processo.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Processo excluído');
      loadProcessos();
      loadBoards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir processo');
    }
  };

  const openNewBoardModal = () => {
    setEditingBoard(null);
    setBoardFormData(EMPTY_BOARD_FORM);
    setShowBoardFormModal(true);
  };

  const openConfigBoardModal = () => {
    if (!selectedBoard) return;
    setEditingBoard(selectedBoard);
    setBoardFormData({
      name: selectedBoard.name || '',
      entity: selectedBoard.entity || '',
      document: selectedBoard.document || '',
      responsible: selectedBoard.responsible || '',
      color: selectedBoard.color || 'Roxo',
      locked: !!selectedBoard.locked,
      users: (selectedBoard.users || []).join(', '),
      description: selectedBoard.description || '',
    });
    setShowBoardFormModal(true);
  };

  const closeBoardFormModal = () => {
    setShowBoardFormModal(false);
    setEditingBoard(null);
    setBoardFormData(EMPTY_BOARD_FORM);
  };

  const handleBoardSubmit = async () => {
    if (!boardFormData.name.trim()) {
      toast.error('Informe o nome do quadro');
      return;
    }
    try {
      setSavingBoard(true);
      const token = localStorage.getItem('token');
      const payload = {
        ...boardFormData,
        users: boardFormData.users
          ? boardFormData.users.split(',').map((u) => u.trim()).filter(Boolean)
          : [],
      };

      if (editingBoard) {
        const response = await axios.put(`${API}/api/processos-boards/${editingBoard.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Quadro atualizado com sucesso');
        await loadBoards();
        setSelectedBoardId(response.data.id);
      } else {
        const response = await axios.post(`${API}/api/processos-boards`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Quadro criado com sucesso');
        await loadBoards();
        setSelectedBoardId(response.data.id);
      }
      closeBoardFormModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar quadro');
    } finally {
      setSavingBoard(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const updateBoardField = (field, value) => setBoardFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="h-6 w-6" />
            Gestão de Processos
          </h1>
          <p className="text-sm text-gray-500">Processos jurídicos (judiciais, administrativos e extrajudiciais)</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" /> Novo processo
        </Button>
      </div>

      <Card>
        <CardContent className="py-3 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 whitespace-nowrap">Quadro atual:</span>
            <Select value={selectedBoardId || 'all'} onValueChange={(v) => setSelectedBoardId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os quadros</SelectItem>
                {boards.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}{b.locked ? ' 🔒' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedBoard && (
            <>
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-blue-500" />
                <div>
                  <div className="text-xs text-gray-500">Processos ativos</div>
                  <div className="font-semibold">{selectedBoard.processos_ativos}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <div>
                  <div className="text-xs text-gray-500">Prazos próximos</div>
                  <div className="font-semibold">{selectedBoard.prazos_proximos}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Gavel className="h-4 w-4 text-purple-500" />
                <div>
                  <div className="text-xs text-gray-500">Audiências agendadas</div>
                  <div className="font-semibold">{selectedBoard.audiencias_agendadas}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-green-500" />
                <div>
                  <div className="text-xs text-gray-500">Processos encerrados</div>
                  <div className="font-semibold">{selectedBoard.processos_encerrados}</div>
                </div>
              </div>
            </>
          )}

          <div className="ml-auto flex items-center gap-2">
            {selectedBoard && (
              <Button variant="outline" size="sm" onClick={openConfigBoardModal}>
                <Settings className="h-4 w-4 mr-2" /> Configurar quadro
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={openNewBoardModal}>
              <Plus className="h-4 w-4 mr-2" /> Novo quadro
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Buscar por título, número ou código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={filtroStatus || 'all'} onValueChange={(v) => setFiltroStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Todos os status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Processo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Risco</TableHead>
                <TableHead>Valor da causa</TableHead>
                <TableHead>Contrato vinculado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Carregando...</TableCell></TableRow>
              ) : filteredProcessos.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Nenhum processo encontrado</TableCell></TableRow>
              ) : filteredProcessos.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/juridico/processos/${p.id}`)}
                >
                  <TableCell>
                    <div className="font-medium">{p.titulo}</div>
                    <div className="text-xs text-gray-500">{p.numero_processo || 'Sem número'}</div>
                  </TableCell>
                  <TableCell>{p.tipo_processo}</TableCell>
                  <TableCell><Badge className={STATUS_COLOR[p.status] || 'bg-gray-100 text-gray-800'}>{p.status}</Badge></TableCell>
                  <TableCell><Badge className={RISCO_COLOR[p.risco] || 'bg-gray-100 text-gray-800'}>{p.risco}</Badge></TableCell>
                  <TableCell>{formatCurrency(p.valor_causa)}</TableCell>
                  <TableCell>
                    {p.contract_title ? (
                      <span className="text-sm">{p.contract_title}{p.contract_number ? ` (${p.contract_number})` : ''}</span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/juridico/processos/${p.id}`)} title="Visualizar">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(p)} title="Editar">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} title="Excluir">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showFormModal} onOpenChange={(open) => !open && closeFormModal()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProcesso ? 'Editar processo' : 'Novo processo'}</DialogTitle>
            <DialogDescription>
              Cadastro do processo jurídico. Partes, audiências, prazos, movimentações e documentos são gerenciados na página de detalhe do processo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Título resumido *</Label>
              <Input value={formData.titulo} onChange={(e) => updateField('titulo', e.target.value)} required />
            </div>

            <div className="space-y-2">
              <Label>Quadro</Label>
              <Select value={formData.board_id || 'none'} onValueChange={(v) => updateField('board_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Nenhum quadro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum quadro</SelectItem>
                  {boards.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Número do processo</Label>
              <Input value={formData.numero_processo} onChange={(e) => updateField('numero_processo', e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Código interno</Label>
              <Input value={formData.codigo_interno} onChange={(e) => updateField('codigo_interno', e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Tipo do processo</Label>
              <Select value={formData.tipo_processo} onValueChange={(v) => updateField('tipo_processo', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_PROCESSO_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Assunto principal</Label>
              <Input value={formData.assunto_principal} onChange={(e) => updateField('assunto_principal', e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Tribunal / Órgão</Label>
              <Input value={formData.tribunal} onChange={(e) => updateField('tribunal', e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Comarca</Label>
              <Input value={formData.comarca} onChange={(e) => updateField('comarca', e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => updateField('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Risco</Label>
              <Select value={formData.risco} onValueChange={(v) => updateField('risco', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RISCO_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Grau de sigilo</Label>
              <Select value={formData.grau_sigilo} onValueChange={(v) => updateField('grau_sigilo', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GRAU_SIGILO_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Probabilidade de perda</Label>
              <Select value={formData.probabilidade_perda || 'none'} onValueChange={(v) => updateField('probabilidade_perda', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Não definida" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não definida</SelectItem>
                  {PROBABILIDADE_PERDA_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Valor da causa</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">R$</span>
                <Input
                  className="pl-8"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={formatCurrencyDisplay(formData.valor_causa)}
                  onChange={(e) => updateField('valor_causa', parseCurrencyInput(e.target.value))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Valor provisionado</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">R$</span>
                <Input
                  className="pl-8"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={formatCurrencyDisplay(formData.valor_provisionado)}
                  onChange={(e) => updateField('valor_provisionado', parseCurrencyInput(e.target.value))}
                />
              </div>
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label>Contrato vinculado</Label>
              <Select value={formData.contract_id || 'none'} onValueChange={(v) => updateField('contract_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Nenhum contrato vinculado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum contrato vinculado</SelectItem>
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.title}{c.contract_number ? ` (${c.contract_number})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label>Resumo do caso</Label>
              <Textarea rows={3} value={formData.resumo_caso} onChange={(e) => updateField('resumo_caso', e.target.value)} />
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label>Tese principal</Label>
              <Textarea rows={3} value={formData.tese_principal} onChange={(e) => updateField('tese_principal', e.target.value)} />
            </div>
          </form>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeFormModal}>Cancelar</Button>
            <Button type="button" disabled={submitting} onClick={handleSubmit}>
              {submitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBoardFormModal} onOpenChange={(open) => !open && closeBoardFormModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBoard ? 'Configurar quadro' : 'Novo quadro jurídico'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Nome do quadro *</Label>
              <Input value={boardFormData.name} onChange={(e) => updateBoardField('name', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Empresa ou pessoa relacionada</Label>
              <Input value={boardFormData.entity} onChange={(e) => updateBoardField('entity', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>CNPJ ou CPF</Label>
              <Input value={boardFormData.document} onChange={(e) => updateBoardField('document', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Responsável principal</Label>
              <Input value={boardFormData.responsible} onChange={(e) => updateBoardField('responsible', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Cor do quadro</Label>
              <Select value={boardFormData.color} onValueChange={(v) => updateBoardField('color', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOARD_COLOR_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label className="flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> Quadro confidencial</Label>
              <Select value={boardFormData.locked ? 'Sim' : 'Não'} onValueChange={(v) => updateBoardField('locked', v === 'Sim')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Não">Não</SelectItem>
                  <SelectItem value="Sim">Sim</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Usuários com acesso (separados por vírgula)</Label>
              <Textarea rows={2} value={boardFormData.users} onChange={(e) => updateBoardField('users', e.target.value)} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Descrição</Label>
              <Textarea rows={3} value={boardFormData.description} onChange={(e) => updateBoardField('description', e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeBoardFormModal}>Cancelar</Button>
            <Button type="button" disabled={savingBoard} onClick={handleBoardSubmit}>
              {savingBoard ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoProcessosPage;
